/**
 * `bosun-search` `/v1/match` — turning its answer into something the RFQ table can show.
 *
 * Pure functions only. The transport is a same-origin `/search` path that Vite proxies to the
 * service with the bearer token injected server-side (`vite.config.ts`), so the browser holds no
 * credential and never makes a cross-origin call — the service answers `401` without the token
 * and `405` to a browser preflight, both measured 2026-08-12.
 *
 * ---- The contract, measured against the deployed service ----
 *
 *     POST {service}/v1/match   {"items": ["rubber hand gloves"]}
 *       → { "results": [ { "query": "rubber hand gloves",
 *                          "matches": [ { "item_code": "1901020003",
 *                                         "item_name": "GLOVES NATURAL RUBBER LONG",
 *                                         "item_group": "RUBBER GLOVES", "stock_uom": "PR",
 *                                         "impa_number": null, "score": 0.857993 }, …10 ] } ] }
 *
 * It differs from `docs/bosun-qdrant-search-service.md` §3.2 in every way that matters: the key
 * is `matches`, there is no singular `match`, no `confidence`, and no `band`. Deciding what is
 * good enough is the caller's job — this file.
 *
 * ---- Two rules, both learned the hard way ----
 *
 * **Join on the echoed `query`, never on position.** The request is a flat array with no `ref`.
 * The service echoes each query back, and that is the only safe key: a response that came back
 * short, or skipped an unmatchable line, would otherwise shift one line's item onto another —
 * invisible on screen, and it ends up on a purchase order.
 *
 * **Trust the rank, not the number.** The score is not comparable across queries:
 *
 *     rubber hand gloves                   correct item @ 0.858
 *     WATER MINERAL 500 ML 24 BOT          correct item @ 0.839
 *     A3 Laminator Machine for 125 Micron  correct item @ 0.118   ← ranked FIRST
 *
 * An earlier version of this logic dropped anything under 0.15 and so discarded the laminator
 * that the service had ranked first, which was then reported as the search being broken. It was
 * not. So NO absolute bar is applied to what is offered or proposed: the top-ranked match is the
 * default and the whole ranked shortlist is shown, whatever they scored. `AUTO_APPLY_SCORE`
 * survives as a presentation threshold only — below it a match is proposed with a visible
 * low-confidence flag, never withheld. Nothing in this path writes to the server unattended.
 */

/** One catalogue item the matcher offered for a line of customer text. */
export interface MatchCandidate {
  item_code: string
  item_name: string
  item_group: string
  stock_uom: string
  score: number
}

export interface ItemMatch {
  /**
   * The TOP-RANKED match, whatever it scored — `null` only when the service matched nothing.
   *
   * This is a proposal, never a write. `LineRow` renders it pre-selected and `RfqPage`'s
   * "Accept all" can apply it in bulk, but both are acts a purchaser takes; nothing here reaches
   * the server on its own. Withholding a weak top match (as an absolute gate used to) hid correct
   * answers — the A3 laminator below — without preventing anything, because the purchaser then
   * had to pick the very same row by hand.
   */
  item_code: string | null
  /** The top match's real score, `null` only when there is no match. Never nulled for being low. */
  confidence: number | null
  /**
   * True when `item_code` scored below `AUTO_APPLY_SCORE` — the signal that keeps a weak default
   * honest on screen. The UI must show it, and must keep showing it once the match is applied:
   * a low-confidence match swept in by "Accept all" looks exactly like a confident one otherwise.
   */
  lowConfidence: boolean
  /** Ranked alternatives to choose from — what a purchaser checks a borderline match against. */
  candidates: MatchCandidate[]
}

/**
 * The bar a match must clear to be presented WITHOUT a low-confidence warning.
 *
 * It used to gate whether the top match was proposed at all, which hid correct answers — the two
 * laminators below scored 0.118 and 0.102 and were right. It now gates the FLAG instead: every
 * top match is proposed, and one under this bar is proposed visibly marked. `rubber hand gloves`
 * @ 0.858 clears it; `WATER MINERAL 500 ML 24 BOT` @ 0.839 does not, and is flagged.
 */
export const AUTO_APPLY_SCORE = 0.85

/**
 * How many to offer. The service sends ten and we now show all of them (fix list point 1): with
 * rate and stock on each row, the shortlist is something a purchaser compares across, not just
 * skims — and a correct match sitting fourth was previously unreachable without the catalogue
 * picker.
 */
const MAX_CANDIDATES = 10

const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '')

function toCandidate(row: unknown): MatchCandidate | null {
  if (typeof row !== 'object' || row === null) return null
  const r = row as Record<string, unknown>
  const code = str(r.item_code)
  const score = r.score
  // No code cannot be applied; no score cannot be ranked. Neither is guessed at.
  if (code === '' || typeof score !== 'number' || !Number.isFinite(score)) return null
  return {
    item_code: code,
    item_name: str(r.item_name),
    item_group: str(r.item_group),
    stock_uom: str(r.stock_uom),
    score: Math.min(1, Math.max(0, score)),
  }
}

/**
 * The service's response → one result per query text.
 *
 * `null` means the payload was not recognisable at all, which every caller treats the same way:
 * leave the lines as they are. A single query missing from the response is not an error — that
 * line simply keeps no match.
 */
export function parseMatchResponse(payload: unknown): Map<string, ItemMatch> | null {
  const root = payload as Record<string, unknown> | null
  const rows = Array.isArray(root?.results) ? (root!.results as unknown[]) : null
  if (!rows) return null

  const byQuery = new Map<string, ItemMatch>()
  for (const row of rows) {
    if (typeof row !== 'object' || row === null) continue
    const r = row as Record<string, unknown>
    const query = typeof r.query === 'string' ? r.query : null
    // Without the echoed query there is no safe way to attach this to a line.
    if (query === null) continue

    const ranked = (Array.isArray(r.matches) ? r.matches : [])
      .map(toCandidate)
      .filter((c): c is MatchCandidate => c !== null)
      .sort((a, b) => b.score - a.score)

    const best = ranked[0]
    byQuery.set(query, {
      item_code: best?.item_code ?? null,
      confidence: best?.score ?? null,
      // `false` when there is no match at all: there is nothing to be unconfident ABOUT, and a
      // `true` here would paint a warning on a row that was simply never matched.
      lowConfidence: best !== undefined && best.score < AUTO_APPLY_SCORE,
      candidates: ranked.slice(0, MAX_CANDIDATES),
    })
  }
  return byQuery
}
